import React, { useEffect, useState, useContext } from "react"
import { Box, CircularProgress, Button, Divider, List, ListItem, TextField, Typography, Modal } from "@mui/material"
import CustomField from "../CustomField"
import dayjs, { Dayjs } from "dayjs"
import { theme } from "../../styles/theme"
import { ThemeProvider } from "@mui/material"
import { EJobStatus, ICustomFieldTemplate, IJob, IJobItems, stageTypes } from "../../entities/job/interface"
import { formatDateTime, useBulkUpdateStatusHook } from "../../entities/job/helper"
import ShiftContext from "../../contexts/ShiftContext"
interface IJobItemList {
    currentJobs?: IJob[] | null
    handleJobUpdate: (job: IJob, items: IJobItems[], customFields: ICustomFieldTemplate[]) => void
    jobStatusMutationLoading?: boolean
    stage?: string
    updateJobStatus: (status: EJobStatus) => void
}

const JobItemList = ({
    currentJobs,
    handleJobUpdate,
    jobStatusMutationLoading,
    stage,
    updateJobStatus,
}: IJobItemList) => {
    const [isEditMode, setIsEditMode] = useState(false)
    const [jobs, setJobs] = useState<any>(currentJobs)
    const [editedWeights, setEditedWeights] = useState<{ [index: number]: { [innerIndex: number]: string } }>({})
    const [formValues, setFormValues] = useState<{
        [index: string]: { [innerIndex: string]: string | Dayjs | boolean | null }
    }>({})
    const [errors, setErrors] = useState<{ [index: string]: { [innerIndex: string]: string } }>({})
    const [isModalOpen, setIsModalOpen] = useState(false)
    const shiftContext = useContext(ShiftContext)
    const threeDecimalRegex = /^(\d*\.\d{0,3}|\d+)$/

    const { handleBulkJobsUpdate, loading } = useBulkUpdateStatusHook()

    // Set initial values from records
    useEffect(() => {
        if (!currentJobs) return
        const initialValues: Record<string, Record<string, string | Dayjs | boolean | null>> = {}

        currentJobs.forEach((job, jobIndex) => {
            job?.customFields?.forEach((field: ICustomFieldTemplate) => {
                const fieldId: string | Dayjs | boolean | null = field.customFieldTemplateId // Assuming this is a string
                const jobKey = jobIndex.toString()

                if (!initialValues[jobKey]) {
                    initialValues[jobKey] = {}
                }

                initialValues[jobKey][fieldId] = (() => {
                    switch (field.type) {
                        case "DATE_TIME":
                        case "DATE_ONLY":
                            return field.value ? dayjs(field.value as string) : null
                        case "TIME_ONLY":
                            return field.value ? formatDateTime(field.value as string) : null
                        case "BOOLEAN":
                            return field.value === true
                        default:
                            return field.value || ""
                    }
                })()
            })
        })
        setFormValues(initialValues)
    }, [currentJobs])

    if (!currentJobs) {
        return <CircularProgress color="secondary" />
    }
    const screen = currentJobs[0]?.jobStatus === "loading" ? "LOADING" : "UNLOADING"

    const handleConfirm = () => {
        // Update currentJobs items with the new quantities
        const updatedJobs = currentJobs?.map((job, jobIndex) => ({
            ...job,
            items: job?.items?.map((item, itemIndex) => {
                return {
                    ...item,
                    quantityLoaded:
                        stage === stageTypes.PICKUP
                            ? parseFloat(editedWeights[jobIndex]?.[itemIndex])
                                ? parseFloat(editedWeights[jobIndex]?.[itemIndex])
                                : item.quantity
                            : item.quantityLoaded,
                    quantityUnloaded:
                        stage === stageTypes.DELIVERY
                            ? parseFloat(editedWeights[jobIndex]?.[itemIndex])
                                ? parseFloat(editedWeights[jobIndex]?.[itemIndex])
                                : item.quantityLoaded
                            : null,
                }
            }),
        }))
        setJobs(updatedJobs)
        setIsEditMode(false) // Exit edit mode
    }

    const handleChange = (jobIndex: number, fieldId: string, value: string | Dayjs | boolean | null) => {
        setFormValues((prev) => ({
            ...prev,
            [jobIndex]: {
                ...prev[jobIndex],
                [fieldId]: value,
            },
        }))
        setErrors((prev) => ({
            ...prev,
            [jobIndex]: {
                ...prev[jobIndex],
                [fieldId]: "", // Reset error on change
            },
        }))
    }

    const validateForm = (): boolean => {
        const newErrors: {
            [jobIndex: number]: { [fieldId: string]: string }
        } = {}

        currentJobs?.forEach((job, jobIndex) => {
            job.customFields.forEach((field: ICustomFieldTemplate) => {
                const isEditable = field.driverVisibility === "EDITABLE"
                const isVisible = field.requiredStage === screen && field.driverVisibility !== "HIDDEN"
                const value = formValues[jobIndex]?.[field.customFieldTemplateId]

                if (isVisible && isEditable && !value && field.type !== "BOOLEAN") {
                    if (!newErrors[jobIndex]) {
                        newErrors[jobIndex] = {}
                    }
                    newErrors[jobIndex][field.customFieldTemplateId] = "This field is required"
                }
            })
        })

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const processJobs = () => {
        const updatedJobs: IJob[] = []

        for (const [jobIndex, job] of jobs.entries()) {
            try {
                const items =
                    job.items?.map((item: IJobItems, itemIndex: number) => {
                        const editedWeight = parseFloat(editedWeights[jobIndex]?.[itemIndex])

                        return {
                            ...item,
                            quantityLoaded:
                                stage === stageTypes.PICKUP
                                    ? !isNaN(editedWeight)
                                        ? editedWeight
                                        : item.quantity
                                    : item.quantityLoaded,
                            quantityUnloaded:
                                stage === stageTypes.DELIVERY
                                    ? !isNaN(editedWeight)
                                        ? editedWeight
                                        : item.quantityLoaded
                                    : null,
                        }
                    }) || []

                // Prepare updated custom fields
                const updatedRecords = job.customFields.map((field: ICustomFieldTemplate) => ({
                    ...field,
                    value: formValues[jobIndex]?.[field.customFieldTemplateId] ?? field.value,
                }))

                // Store updated job
                const updatedJob = {
                    ...job,
                    items,
                    customFields: updatedRecords,
                    jobStatus: stage === stageTypes.PICKUP ? EJobStatus.loaded : EJobStatus.unloaded,
                }

                updatedJobs.push(updatedJob)

                // Await job update
                if (handleJobUpdate) {
                    handleJobUpdate(job, items, updatedRecords)
                } else {
                    console.warn("handleJobUpdate function is not available.")
                }
            } catch (jobError) {
                console.error(`Error updating job at index ${jobIndex}:`, jobError)
            }
        }

        return updatedJobs
    }

    const handleSubmit = async () => {
        try {
            if (!validateForm()) {
                console.error("Form not submitted due to validation errors", formValues)
                return
            }

            if (!jobs || jobs.length === 0) {
                console.warn("No jobs available to update.")
                return
            }

            //value should not be zero
            for (const [jobIndex, job] of jobs.entries()) {
                for (const [itemIndex] of job.items.entries()) {
                    const editedWeight = parseFloat(editedWeights[jobIndex]?.[itemIndex])
                    if (editedWeight === 0) {
                        setIsModalOpen(true)
                        return
                    }
                }
            }

            const updatedJobs = processJobs()

            const jobIds = updatedJobs.filter((job) => !job.isJobCompleted).map((job) => job._id)
            const jobStatus = stage === stageTypes.PICKUP ? EJobStatus.loaded : EJobStatus.unloaded
            await handleBulkJobsUpdate(jobIds, jobStatus)

            updateJobStatus(jobStatus)

            shiftContext?.updateJob(updatedJobs)
        } catch (error) {
            console.error("Unexpected error in handleSubmit:", error)
        }
    }

    const handleModalClose = () => {
        setIsModalOpen(false)
    }

    return (
        <ThemeProvider theme={theme}>
            <Box
                width={"100%"}
                display={"flex"}
                flexDirection={"column"}
                marginBottom={3}
                height={"100%"}
                sx={{ overflowY: "auto" }}
            >
                <List>
                    {currentJobs?.map((job: IJob, jobIndex: number) => (
                        <React.Fragment key={job._id}>
                            {job.items?.map((item, itemIndex) => {
                                const getFieldValue = () => {
                                    const editedValue = editedWeights[jobIndex]?.[itemIndex]
                                    if (editedValue !== undefined) return editedValue // Keep as string to avoid unwanted formatting

                                    let value
                                    if (stage === stageTypes.PICKUP) {
                                        value =
                                            item?.quantityLoaded === null || item?.quantity === item?.quantityLoaded
                                                ? item?.quantity
                                                : item?.quantityLoaded
                                    } else {
                                        value =
                                            item?.quantityUnloaded === null || item?.quantity === item?.quantityUnloaded
                                                ? item?.quantityLoaded
                                                : item?.quantityUnloaded
                                    }
                                    return value ? parseFloat(value) : "0.000" // Ensure default formatting
                                }

                                return (
                                    <ListItem key={`${jobIndex}-${item.itemId}`} style={{ marginTop: 1 }}>
                                        <Box display="flex" flexDirection="column" width="100%">
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                width="100%"
                                                paddingX={{ xs: "6%", sm: "4%", md: "2%" }}
                                            >
                                                <Box
                                                    width="60%"
                                                    display="flex"
                                                    flexDirection="column"
                                                    justifyContent="center"
                                                >
                                                    <Typography color="white">{item.name}</Typography>
                                                    <Typography color="white">{item.code}</Typography>
                                                </Box>
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    alignItems="center"
                                                    width="30%"
                                                    justifyContent="center"
                                                >
                                                    {isEditMode ? (
                                                        <TextField
                                                            value={getFieldValue()}
                                                            onChange={(e) => {
                                                                const value = e.target.value
                                                                if (value === "" || threeDecimalRegex.test(value)) {
                                                                    setEditedWeights((prevWeights) => ({
                                                                        ...prevWeights,
                                                                        [jobIndex]: {
                                                                            ...prevWeights[jobIndex],
                                                                            [itemIndex]: value,
                                                                        },
                                                                    }))
                                                                }
                                                            }}
                                                            variant="outlined"
                                                            size="small"
                                                            type="text"
                                                            sx={{
                                                                input: {
                                                                    color: "secondary.main",
                                                                    backgroundColor: "white",
                                                                },
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography color="white">{getFieldValue()}</Typography>
                                                    )}
                                                    <Typography color="white">{item.unit}</Typography>
                                                </Box>
                                            </Box>
                                            <Divider
                                                sx={{
                                                    opacity: 0.5,
                                                    borderWidth: 1,
                                                    borderStyle: "dashed",
                                                    marginTop: 3,
                                                    width: "100%",
                                                    borderColor: "white",
                                                }}
                                            />
                                        </Box>
                                    </ListItem>
                                )
                            })}
                            <Box
                                width={"100%"}
                                display="flex"
                                justifyContent="start"
                                flexDirection={"column"}
                                paddingX={3}
                            >
                                {job.customFields.map((field: ICustomFieldTemplate) => {
                                    const isEditable = field.driverVisibility === "EDITABLE"
                                    const isVisibility =
                                        field.requiredStage === screen && field.driverVisibility !== "HIDDEN"
                                    return (
                                        isVisibility && (
                                            <Box key={field.customFieldTemplateId} marginBottom={2}>
                                                <Typography color="white">{field.name}</Typography>

                                                <CustomField
                                                    field={field}
                                                    value={formValues[jobIndex]?.[field.customFieldTemplateId] ?? null}
                                                    error={errors[jobIndex]?.[field.customFieldTemplateId]}
                                                    onChange={(fieldId, value) =>
                                                        handleChange(jobIndex, fieldId, value)
                                                    }
                                                    isEditable={isEditable}
                                                    type={field.type}
                                                />
                                            </Box>
                                        )
                                    )
                                })}
                            </Box>
                        </React.Fragment>
                    ))}
                </List>
            </Box>

            <Box
                width={"100%"}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                marginBottom={3}
                gap={2}
                paddingX={2}
            >
                <Box width={"50%"} display={"flex"} justifyContent={"center"}>
                    <Button
                        size="large"
                        variant="outlined"
                        disabled={currentJobs[0]?.isJobCompleted}
                        onClick={() => (isEditMode ? handleConfirm() : setIsEditMode(true))}
                        sx={{
                            width: "100%",
                            color: "white",
                            borderRadius: 3,
                            borderWidth: 2,
                            borderColor: "white",
                            textTransform: "none",
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        {isEditMode ? "Confirm" : "Edit Qty"}
                    </Button>
                </Box>

                <Box width={"50%"} display={"flex"} justifyContent={"center"}>
                    <Button
                        size="large"
                        variant="outlined"
                        onClick={() => handleSubmit()}
                        sx={{
                            width: "100%",
                            color: "white",
                            borderRadius: 3,
                            borderWidth: 2,
                            borderColor: "white",
                            textTransform: "none",
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        {jobStatusMutationLoading || loading ? (
                            <CircularProgress color="secondary" />
                        ) : stage === stageTypes.PICKUP ? (
                            "Load All"
                        ) : (
                            "Unload All"
                        )}
                    </Button>
                </Box>
            </Box>
            {/* Modal for warning */}
            <Modal
                open={isModalOpen}
                onClose={handleModalClose}
                style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                <Box bgcolor="white" p={10} boxShadow={24} borderRadius={2} textAlign="center">
                    <Typography variant="h4" color="black" style={{ fontWeight: "bold" }}>
                        Warning!
                    </Typography>
                    <Typography color="black" variant="h5" style={{ marginTop: 2 }}>
                        The value cannot be 0.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleModalClose}
                        sx={{ marginTop: 2, paddingX: 4 }}
                    >
                        OK
                    </Button>
                </Box>
            </Modal>
        </ThemeProvider>
    )
}

export default JobItemList
